/*LIBRARY*/
import { useEffect, useRef, useCallback } from "react";
import { connect } from "react-redux";

/*COMPONENT*/
import IMAGE_FULL from "./IMAGE_FULL";

/*REDUX*/
import { set_value_properties } from "../../App/actions/propertiesActions";

/*CONST*/
const CLUSTERING_DEBOUNCE_MS = 150;

const L3_W = 50;
const L3_H = 70;
const L2_W = 110;
const L2_H = 60;

const L1_W = 14;
const L1_H = 14;

// ---------------------------------------------------------------------------
// Heatmap constants & Buffer Config
// ---------------------------------------------------------------------------

const HEATMAP_OPACITY = 0.4;
const HEATMAP_MIN_HIDDEN = 1;
const CANVAS_BUFFER = 500;
const MARKER_BUFFER = 150;

const HEATMAP_LAYERS = [
  { blur: 22, r: 28, fill: "#22c55e", min_count: 1 },
  { blur: 14, r: 20, fill: "#eab308", min_count: 2 },
  { blur: 8, r: 14, fill: "#ef4444", min_count: 4 },
];

const HEATMAP_GRID_PX = 40;

// ---------------------------------------------------------------------------
// iOS Safari Optimization: Pre-rendered Radial Brushes
// ---------------------------------------------------------------------------

const hexToRgb = (hex) => {
  const bigint = parseInt(hex.replace("#", ""), 16);
  return `${(bigint >> 16) & 255}, ${(bigint >> 8) & 255}, ${bigint & 255}`;
};

const BRUSH_CACHE = HEATMAP_LAYERS.map((layer) => {
  const canvas = document.createElement("canvas");
  const size = (layer.r + layer.blur) * 2;
  canvas.width = size;
  canvas.height = size;

  const ctx = canvas.getContext("2d");
  const center = size / 2;

  const gradient = ctx.createRadialGradient(
    center,
    center,
    layer.r * 0.2,
    center,
    center,
    layer.r + layer.blur,
  );

  const rgb = hexToRgb(layer.fill);

  gradient.addColorStop(0, `rgba(${rgb}, 1)`);
  gradient.addColorStop(0.5, `rgba(${rgb}, 0.8)`);
  gradient.addColorStop(1, `rgba(${rgb}, 0)`);

  ctx.fillStyle = gradient;
  ctx.beginPath();
  ctx.arc(center, center, layer.r + layer.blur, 0, Math.PI * 2);
  ctx.fill();

  return { canvas, offset: center };
});

// ---------------------------------------------------------------------------
// Marker Templates
// ---------------------------------------------------------------------------

const HTML_CIRCLE_ACTIVITY = `
  <div style="width: 10px; height: 10px; background-color: #FFFFFF; border: 2px solid #ccc; border-radius: 50%; box-shadow: 0 1px 2px rgba(0,0,0,0.3);"></div>
`;

const getMarkerText = (feature) => {
  const title = feature?.title || feature?.properties?.title || "";
  const desc = feature?.description || feature?.properties?.description || "";
  const combined = `${title} ${desc}`;

  const emojiRegex =
    /[\u{1F300}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{1F1E6}-\u{1F1FF}\u{1F004}-\u{1F0CF}\u{1F201}-\u{1F251}\u{1F600}-\u{1F64F}\u{1F680}-\u{1F6FF}][\u{FE0F}\u{1F3FB}-\u{1F3FF}\u{200D}]*/gu;
  const hashtagRegex = /#(\w{1,10})\b/;

  const emojiMatches = combined.match(emojiRegex);
  const hashtagMatch = combined.match(hashtagRegex);

  if (emojiMatches && emojiMatches.length > 0) {
    const top3Emojis = emojiMatches
      .slice(0, 3)
      .map((e) => e.replace(/\uFE0F/g, "") + "\uFE0F")
      .join("");

    if (hashtagMatch) return `#${hashtagMatch[1]} ${top3Emojis}`;
    return top3Emojis;
  }

  if (hashtagMatch) return `#${hashtagMatch[1]}`;

  const firstWord = title.trim().split(/\s+/)[0] || "";
  return firstWord.substring(0, 15);
};

const getCustomDoubleHTML = (feature) => {
  const textContent = getMarkerText(feature);

  return `
    <div style="
      position: relative; 
      display: flex; 
      flex-direction: column; 
      align-items: center; 
      transform-origin: bottom center;
    ">
      <div style="
        background-color: #FFFFFF; 
        border: 2px solid white;
        border-radius: 10px; 
        padding: 1px 4px;
        display: flex; 
        align-items: center; 
        justify-content: center; 
        box-shadow: 0px 2px 4px rgba(0,0,0,0.3);
        font-size: 11px; 
        font-weight: 600;
        color: #333;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
        white-space: nowrap;
        max-width: 110px;
        overflow: hidden;
        text-overflow: ellipsis;
        box-sizing: border-box;
      ">
        ${textContent}
      </div>
      <div style="
        width: 0; 
        height: 0; 
        border-left: 5px solid transparent; 
        border-right: 5px solid transparent; 
        border-top: 6px solid white; 
        margin-top: -1px;
      "></div>
    </div>
  `;
};

const STYLE_CSS = `
  @keyframes popupGrow {
    0% { opacity: 0; transform: scale(0.1) translateY(10px); }
    100% { opacity: 1; transform: scale(1) translateY(0); }
  }
`;

// ---------------------------------------------------------------------------
// Global Helper & L3 image rendering
// ---------------------------------------------------------------------------

const isVideo = (url) => {
  if (!url) return false;
  return /\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(url);
};

const l3HtmlCache = new Map();

export const clearL3Cache = () => l3HtmlCache.clear();

const resolveL3MediaUrl = (feature) => {
  const medias = feature.medias || feature.properties?.medias || [];
  if (medias.length === 0) return null;
  return medias[1] ?? medias[0];
};

const buildL3InnerContent = (mediaUrl) => {
  if (!mediaUrl) {
    return `<div style="width:100%;height:100%;background:#eee;border-radius:4px;"></div>`;
  }

  if (isVideo(mediaUrl)) {
    return `<video
      src="${mediaUrl}"
      autoplay
      loop
      muted
      playsinline
      style="width:100%;height:100%;object-fit:cover;border-radius:4px;background-color:#000;"
    ></video>`;
  }

  return `<img
    src="${mediaUrl}"
    decoding="async"
    fetchpriority="low"
    style="width:100%;height:100%;object-fit:cover;border-radius:4px;"
    alt=""
  />`;
};

const getCustomMarkerHTML = (feature, isSelected) => {
  const mediaUrl = resolveL3MediaUrl(feature);
  const cacheKey = `${mediaUrl}|${isSelected ? 1 : 0}`;

  if (l3HtmlCache.has(cacheKey)) return l3HtmlCache.get(cacheKey);

  const shadowStyle = isSelected
    ? `0 0 0 3px #000, 0px 2px 4px rgba(0,0,0,0.3)`
    : `0px 2px 4px rgba(0,0,0,0.3)`;

  const innerContent = buildL3InnerContent(mediaUrl);

  const html = `
    <div style="
      position: relative; 
      display: flex; 
      flex-direction: column; 
      align-items: center; 
      transform-origin: bottom center; 
    ">
      <div style="
        width: 32px; 
        height: 48px; 
        background-color: #FFFFFF; 
        border: 2px solid white;
        border-radius: 6px; 
        display: flex; 
        align-items: center; 
        justify-content: center; 
        box-shadow: ${shadowStyle}; 
        transition: all 0.2s ease; 
        box-sizing: content-box; 
      ">
        ${innerContent}
      </div>
      <div style="
        width: 0; 
        height: 0; 
        border-left: 6px solid transparent; 
        border-right: 6px solid transparent; 
        border-top: 8px solid white; 
        margin-top: -1px;
      "></div>
    </div>
  `;

  l3HtmlCache.set(cacheKey, html);
  return html;
};

// ---------------------------------------------------------------------------
// Canvas Heatmap helpers
// ---------------------------------------------------------------------------

const bucketPointsForLayers = (points) => {
  if (points.length === 0) return HEATMAP_LAYERS.map(() => []);

  const cells = new Map();
  for (let i = 0; i < points.length; i++) {
    const { x, y } = points[i];
    const col = Math.floor(x / HEATMAP_GRID_PX);
    const row = Math.floor(y / HEATMAP_GRID_PX);
    const key = `${col},${row}`;
    const existing = cells.get(key);
    if (existing) {
      existing.count += 1;
    } else {
      cells.set(key, {
        x: (col + 0.5) * HEATMAP_GRID_PX,
        y: (row + 0.5) * HEATMAP_GRID_PX,
        count: 1,
      });
    }
  }

  return HEATMAP_LAYERS.map(({ min_count }) => {
    const layer_points = [];
    cells.forEach((cell) => {
      if (cell.count >= min_count) {
        layer_points.push({ x: cell.x, y: cell.y });
      }
    });
    return layer_points;
  });
};

const createHeatmapCanvas = (container) => {
  const canvas = document.createElement("canvas");
  canvas.className = "activity-heatmap-canvas";

  canvas.style.cssText = [
    "position:absolute",
    `top:-${CANVAS_BUFFER}px`,
    `left:-${CANVAS_BUFFER}px`,
    "pointer-events:none",
    `opacity:${HEATMAP_OPACITY}`,
    "z-index:5",
    `transform-origin: ${CANVAS_BUFFER}px ${CANVAS_BUFFER}px`,
    "will-change: transform",
  ].join(";");

  const ctx = canvas.getContext("2d");
  container.appendChild(canvas);

  return { canvas, ctx };
};

// ---------------------------------------------------------------------------
// PopupContent - MOVED OUTSIDE THE MAIN COMPONENT
// ---------------------------------------------------------------------------

const PopupContent = ({ feature, on_click_image }) => {
  const safeFeature = feature || {};
  const title =
    safeFeature.title || safeFeature.properties?.title || "No Title";
  const description =
    safeFeature.description || safeFeature.properties?.description || "";
  const medias = safeFeature.medias || safeFeature.properties?.medias || [];

  const user_name =
    safeFeature.user_name || safeFeature.properties?.user_name || "";
  const user_full_name =
    safeFeature.user_full_name ||
    safeFeature.properties?.user_full_name ||
    "User";

  const user_profile =
    safeFeature.user_profile_picture ||
    safeFeature.properties?.user_profile_picture;
  const profilePic = user_profile?.url_compressed || user_profile?.url || "";

  return (
    <div style={{ marginBottom: "16px" }}>
      <div
        style={{
          fontSize: "16px",
          fontWeight: "bold",
          marginBottom: "4px",
          color: "#222",
        }}
      >
        {title}
      </div>
      {description && (
        <div
          style={{
            fontSize: "13px",
            color: "#555",
            marginBottom: "12px",
            lineHeight: "1.4",
          }}
        >
          {description}
        </div>
      )}

      {medias.length > 0 && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: medias.length === 1 ? "1fr" : "1fr 1fr",
            gap: "8px",
            marginBottom: "12px",
          }}
        >
          {medias.map((media, idx) => {
            const isMediaVideo = isVideo(media);

            return (
              <div
                key={idx}
                style={{
                  position: "relative",
                  width: "100%",
                  aspectRatio: "2/3",
                }}
              >
                {isMediaVideo ? (
                  <video
                    src={media}
                    autoPlay
                    loop
                    muted
                    playsInline
                    onClick={() =>
                      on_click_image && on_click_image(media, medias, idx)
                    }
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      borderRadius: "8px",
                      border: "1px solid #f0f0f0",
                      backgroundColor: "#000",
                      cursor: "pointer",
                    }}
                  />
                ) : (
                  <img
                    src={media}
                    alt="📷"
                    onClick={() =>
                      on_click_image && on_click_image(media, medias, idx)
                    }
                    loading="lazy"
                    decoding="async"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      borderRadius: "8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                      fontSize: "25px",
                      cursor: "pointer",
                      border: "1px solid #f0f0f0",
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          marginTop: "12px",
          paddingTop: "12px",
          borderTop: "1px solid #f0f0f0",
        }}
      >
        {profilePic ? (
          <img
            src={profilePic}
            alt={user_full_name}
            loading="lazy"
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              objectFit: "cover",
              backgroundColor: "#eee",
            }}
          />
        ) : (
          <div
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              backgroundColor: "#ccc",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "14px",
              color: "#fff",
              fontWeight: "bold",
            }}
          >
            {user_full_name.charAt(0).toUpperCase()}
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column" }}>
          <span
            style={{
              fontSize: "13px",
              fontWeight: "bold",
              color: "#333",
              lineHeight: "1.2",
            }}
          >
            {user_full_name}
          </span>
          {user_name && (
            <span
              style={{
                fontSize: "11px",
                color: "#777",
                marginTop: "2px",
              }}
            >
              @{user_name}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

const LAYER_ACTIVITIES_APP = ({
  layer,
  properties,
  map_object_string = "map_object",
  set_value_properties,
}) => {
  const {
    features_activities_app,
    feature_activity_selected,
    multi_activity_selected,
    modal_activity,
    modal_activity_double,
    modal_image_preview,

    // We expect these new properties from redux or fallback gracefully
    preview_image_url,
    preview_media_list = [],
    preview_media_index = 0,

    is_show_activity = true,
    is_show_image = false,
  } = properties;

  const map_object = layer[map_object_string];

  const markersRef = useRef({});
  const screenPointsRef = useRef([]);
  const popupRef = useRef(null);
  const lastZoomRef = useRef(null);

  const rafRef = useRef(null);
  const debounceRef = useRef(null);
  const isDraggingRef = useRef(false);

  const heatmapCanvasRef = useRef(null);
  const heatmapCtxRef = useRef(null);

  const heatmapAnchorRef = useRef(null);
  const prevShowActivityRef = useRef(is_show_activity);
  const prevShowImageRef = useRef(is_show_image);

  const touchStateRef = useRef({
    start: null,
    time: 0,
    is_multi: false,
    lastTapTime: 0,
    tapTimeout: null,
  });
  const isTouchModeRef = useRef(false);

  const selectedStateRef = useRef({
    single: feature_activity_selected,
    multi: multi_activity_selected,
  });

  const modalRef = useRef({
    single: modal_activity,
    multi: modal_activity_double,
  });

  useEffect(() => {
    const styleId = "custom-marker-animations-activity";
    if (!document.getElementById(styleId)) {
      const styleSheet = document.createElement("style");
      styleSheet.id = styleId;
      styleSheet.innerText = STYLE_CSS;
      document.head.appendChild(styleSheet);
    }
  }, []);

  useEffect(() => {
    clearL3Cache();
  }, [features_activities_app]);

  const updatePopupPosition = useCallback(() => {
    if (!popupRef.current) return;
    const isSingleOpen =
      modalRef.current.single && selectedStateRef.current.single;
    const isMultiOpen =
      modalRef.current.multi &&
      selectedStateRef.current.multi &&
      selectedStateRef.current.multi.length > 0;

    if (!isSingleOpen && !isMultiOpen) {
      popupRef.current.style.display = "none";
      return;
    }

    const selectedSingleId = isSingleOpen
      ? selectedStateRef.current.single.title
      : null;
    const selectedMultiId = isMultiOpen
      ? selectedStateRef.current.multi[0].title
      : null;

    let targetP = null;
    for (const p of screenPointsRef.current) {
      if (selectedSingleId && p.feature.title === selectedSingleId) {
        targetP = p;
        break;
      }
      if (
        selectedMultiId &&
        p.hasStack &&
        p.stackedFeatures.length > 0 &&
        p.stackedFeatures[0].title === selectedMultiId
      ) {
        targetP = p;
        break;
      }
    }

    if (targetP) {
      const yOffset = "calc(-100% - 13px)";
      popupRef.current.style.display = "block";

      const newTransform = `translate3d(${targetP.x.toFixed(1)}px, ${targetP.y.toFixed(1)}px, 0) translate(-50%, ${yOffset})`;
      if (popupRef.current._cachedTransform !== newTransform) {
        popupRef.current.style.transform = newTransform;
        popupRef.current._cachedTransform = newTransform;
      }
    } else {
      popupRef.current.style.display = "none";
    }
  }, []);

  useEffect(() => {
    selectedStateRef.current = {
      single: feature_activity_selected,
      multi: multi_activity_selected,
    };
    modalRef.current = {
      single: modal_activity,
      multi: modal_activity_double,
    };

    updatePopupPosition();
  }, [
    feature_activity_selected,
    multi_activity_selected,
    modal_activity,
    modal_activity_double,
    updatePopupPosition,
  ]);

  const on_close = useCallback(() => {
    set_value_properties({ key: "modal_activity", value: false });
    set_value_properties({ key: "modal_activity_double", value: false });
    set_value_properties({ key: "feature_activity_selected", value: null });
    set_value_properties({ key: "multi_activity_selected", value: null });
  }, [set_value_properties]);

  const on_close_image = useCallback(() => {
    set_value_properties({ key: "modal_image_preview", value: false });
    set_value_properties({ key: "preview_image_url", value: null });
    set_value_properties({ key: "preview_media_list", value: [] });
    set_value_properties({ key: "preview_media_index", value: 0 });
  }, [set_value_properties]);

  const on_click_image = useCallback(
    (url, mediaList = [], index = 0) => {
      if (url) {
        set_value_properties({ key: "preview_image_url", value: url });
        set_value_properties({
          key: "preview_media_list",
          value: mediaList.length > 0 ? mediaList : [url],
        });
        set_value_properties({ key: "preview_media_index", value: index });
        set_value_properties({ key: "modal_image_preview", value: true });
      }
    },
    [set_value_properties],
  );

  const on_click_single = useCallback(
    (feature) => {
      // Open Activity Modal
      set_value_properties({
        key: "feature_activity_selected",
        value: feature,
      });
      set_value_properties({ key: "multi_activity_selected", value: null });
      set_value_properties({ key: "modal_activity", value: true });
      set_value_properties({ key: "modal_activity_double", value: false });

      // ADD THIS: Force close Property Modals
      set_value_properties({ key: "modal_property", value: false });
      set_value_properties({ key: "modal_property_double", value: false });
      set_value_properties({ key: "feature_property_selected", value: null });
      set_value_properties({ key: "multi_property_selected", value: null });
    },
    [set_value_properties],
  );

  const on_click_double = useCallback(
    (features) => {
      // Open Activity Double Modal
      set_value_properties({ key: "feature_activity_selected", value: null });
      set_value_properties({ key: "multi_activity_selected", value: features });
      set_value_properties({ key: "modal_activity_double", value: true });
      set_value_properties({ key: "modal_activity", value: false });

      // ADD THIS: Force close Property Modals
      set_value_properties({ key: "modal_property", value: false });
      set_value_properties({ key: "modal_property_double", value: false });
      set_value_properties({ key: "feature_property_selected", value: null });
      set_value_properties({ key: "multi_property_selected", value: null });
    },
    [set_value_properties],
  );

  const on_click_empty = useCallback(() => {
    set_value_properties({ key: "feature_activity_selected", value: null });
    set_value_properties({ key: "multi_activity_selected", value: null });
    set_value_properties({ key: "modal_activity", value: false });
    set_value_properties({ key: "modal_activity_double", value: false });
  }, [set_value_properties]);

  const updateHeatmap = useCallback(
    (hidden_on_screen_points, container_width, container_height) => {
      const canvas = heatmapCanvasRef.current;
      const ctx = heatmapCtxRef.current;

      if (!canvas || !ctx) return;

      if (
        !is_show_activity ||
        hidden_on_screen_points.length < HEATMAP_MIN_HIDDEN
      ) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        canvas.style.display = "none";
        return;
      }

      const paddedWidth = container_width + CANVAS_BUFFER * 2;
      const paddedHeight = container_height + CANVAS_BUFFER * 2;

      if (canvas.width !== paddedWidth || canvas.height !== paddedHeight) {
        canvas.width = paddedWidth;
        canvas.height = paddedHeight;
      } else {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }

      const layer_point_arrays = bucketPointsForLayers(hidden_on_screen_points);

      layer_point_arrays.forEach((pts, idx) => {
        if (pts.length === 0) return;

        const brush = BRUSH_CACHE[idx];

        for (let i = 0; i < pts.length; i++) {
          const { x, y } = pts[i];

          const bx = x + CANVAS_BUFFER;
          const by = y + CANVAS_BUFFER;

          ctx.drawImage(
            brush.canvas,
            Math.floor(bx - brush.offset),
            Math.floor(by - brush.offset),
          );
        }
      });

      canvas.style.display = "block";
    },
    [is_show_activity],
  );

  const updateCollisionsAndRender = useCallback(() => {
    if (!map_object || !markersRef.current) return;

    const canvasObj = map_object.getCanvas();
    const canvasWidth = canvasObj.clientWidth;
    const canvasHeight = canvasObj.clientHeight;

    if (!is_show_activity) {
      screenPointsRef.current.forEach((p) => {
        p.isVisible = false;
      });
      Object.values(markersRef.current).forEach((markerElement) => {
        if (markerElement) markerElement.style.display = "none";
      });
      updatePopupPosition();
      updateHeatmap([], canvasWidth, canvasHeight);
      return;
    }

    const selectedSingle = selectedStateRef.current.single;
    const selectedMulti = selectedStateRef.current.multi;

    let forceHeatmapUpdate = false;
    if (
      prevShowActivityRef.current !== is_show_activity ||
      prevShowImageRef.current !== is_show_image
    ) {
      forceHeatmapUpdate = true;
      prevShowActivityRef.current = is_show_activity;
      prevShowImageRef.current = is_show_image;
    }

    let isZoomChange = false;
    const currentZoom = map_object.getZoom();
    if (
      lastZoomRef.current === null ||
      Math.abs(lastZoomRef.current - currentZoom) > 0.05
    ) {
      isZoomChange = true;
      lastZoomRef.current = currentZoom;
    }

    const currentBearing = map_object.getBearing();
    const currentPitch = map_object.getPitch();

    let isBearingChange = false;
    let isPitchChange = false;
    if (heatmapAnchorRef.current) {
      if (Math.abs(currentBearing - heatmapAnchorRef.current.bearing) > 0.5) {
        isBearingChange = true;
      }
      if (Math.abs(currentPitch - heatmapAnchorRef.current.pitch) > 0.5) {
        isPitchChange = true;
      }
    }

    let isSignificantPan = false;
    if (heatmapAnchorRef.current) {
      const currentAnchorPx = map_object.project(
        heatmapAnchorRef.current.lngLat,
      );
      const dx = Math.abs(currentAnchorPx.x - heatmapAnchorRef.current.px.x);
      const dy = Math.abs(currentAnchorPx.y - heatmapAnchorRef.current.px.y);
      if (dx > CANVAS_BUFFER * 0.6 || dy > CANVAS_BUFFER * 0.6) {
        isSignificantPan = true;
      }
    }

    const shouldUpdateHeatmap =
      isZoomChange ||
      isBearingChange ||
      isPitchChange ||
      forceHeatmapUpdate ||
      !heatmapAnchorRef.current ||
      isSignificantPan;

    const baseType = is_show_image ? "L3" : "L2";
    const baseW = is_show_image ? L3_W : L2_W;
    const baseH = is_show_image ? L3_H : L2_H;

    screenPointsRef.current.forEach((p) => {
      const coords = p.feature.geometry?.coordinates || [0, 0];
      const point = map_object.project(coords);

      p.x = point.x;
      p.y = point.y;
      p.base_x = point.x;
      p.base_y = point.y;

      p.hasStack = false;
      p.stackedFeatures = [p.feature];
      p.type = baseType;

      p.isOffScreen =
        p.x < -CANVAS_BUFFER ||
        p.x > canvasWidth + CANVAS_BUFFER ||
        p.y < -CANVAS_BUFFER ||
        p.y > canvasHeight + CANVAS_BUFFER;

      p.isOffMarkerScreen =
        p.x < -MARKER_BUFFER ||
        p.x > canvasWidth + MARKER_BUFFER ||
        p.y < -MARKER_BUFFER ||
        p.y > canvasHeight + MARKER_BUFFER;
    });

    const onScreenPoints = screenPointsRef.current.filter(
      (p) => !p.isOffScreen,
    );
    const sortedPoints = onScreenPoints.sort((a, b) => a.y - b.y);
    const visibleSpots = [];
    const hidden_on_screen_points = [];

    sortedPoints.forEach((p) => {
      if (p.isOffMarkerScreen) {
        p.isVisible = false;
        hidden_on_screen_points.push({ x: p.x, y: p.y });
        return;
      }

      let canFitBase = true;
      let canFitL1 = true;
      let blockingSpot = null;
      let minStackDist = Infinity;

      for (const spot of visibleSpots) {
        const dx = Math.abs(p.x - spot.x);
        const dy = Math.abs(p.y - spot.y);

        const spotW =
          spot.type === "L3" ? L3_W : spot.type === "L2" ? L2_W : L1_W;
        const spotH =
          spot.type === "L3" ? L3_H : spot.type === "L2" ? L2_H : L1_H;

        if (dx < (baseW + spotW) / 2 && dy < (baseH + spotH) / 2) {
          canFitBase = false;
        }
        if (dx < (L1_W + spotW) / 2 && dy < (L1_H + spotH) / 2) {
          canFitL1 = false;
          const dist = dx * dx + dy * dy;
          if (dist < minStackDist) {
            minStackDist = dist;
            blockingSpot = spot;
          }
        }
      }

      if (canFitBase) {
        p.isVisible = true;
        p.type = baseType;
        visibleSpots.push(p);
      } else if (canFitL1) {
        p.isVisible = true;
        p.type = "L1";
        visibleSpots.push(p);
      } else {
        p.isVisible = false;
        hidden_on_screen_points.push({ x: p.x, y: p.y });

        if (blockingSpot) {
          blockingSpot.hasStack = true;
          blockingSpot.stackedFeatures.push(p.feature);
          if (blockingSpot.type === "L1") {
            blockingSpot.type = baseType;
          }
        }
      }
    });

    screenPointsRef.current.forEach((p) => {
      const markerId = `marker_act_${p.index}`;
      let markerElement = markersRef.current[markerId];

      if (!markerElement) {
        markerElement = document.createElement("div");
        markerElement.className = "mapboxgl-marker-s-activity-community";
        markerElement.style.position = "absolute";
        markerElement.style.transition = "none";
        markerElement.style.willChange = "transform";
        markerElement.style.pointerEvents = "none";
        map_object.getContainer().appendChild(markerElement);
        markersRef.current[markerId] = markerElement;
      }

      if (!p.isVisible || p.isOffMarkerScreen) {
        if (markerElement.style.display !== "none")
          markerElement.style.display = "none";
        return;
      }

      if (markerElement.style.display !== "block")
        markerElement.style.display = "block";

      let targetType = "marker_activity";
      if (p.type === "L2") targetType = "double";
      else if (p.type === "L1") targetType = "circle_activity";

      const selectedSingleId = selectedSingle ? selectedSingle.title : null;
      const selectedMultiId =
        selectedMulti && selectedMulti.length > 0
          ? selectedMulti[0].title
          : null;
      const currentStackId =
        p.stackedFeatures && p.stackedFeatures.length > 0
          ? p.stackedFeatures[0].title
          : null;

      let isSelected = false;
      if (p.hasStack) {
        if (
          selectedMultiId &&
          currentStackId &&
          selectedMultiId === currentStackId
        )
          isSelected = true;
      } else {
        if (selectedSingleId && p.feature.title === selectedSingleId)
          isSelected = true;
      }

      let iconHTML = "";
      let offsetTransform = "translate(-50%, -100%)";
      let zIndex = "20";

      if (targetType === "circle_activity") {
        offsetTransform = "translate(-50%, -50%)";
      }

      if (isSelected) {
        iconHTML = `<div style="display: none;"></div>`;
        zIndex = "1";
      } else {
        if (targetType === "double") {
          iconHTML = getCustomDoubleHTML(p.feature);
          zIndex = "15";
        } else if (targetType === "circle_activity") {
          iconHTML = HTML_CIRCLE_ACTIVITY;
          zIndex = "10";
        } else {
          iconHTML = getCustomMarkerHTML(p.feature, false);
        }
      }

      const mediaUrl =
        targetType === "marker_activity"
          ? (resolveL3MediaUrl(p.feature) ?? "")
          : "";
      const currentSelectedState = isSelected ? "true" : "false";
      const innerHash = `${targetType}-${currentSelectedState}-${mediaUrl}`;
      const prevHash = markerElement.getAttribute("data-hash");

      if (prevHash !== innerHash) {
        markerElement.innerHTML = `<div style="width:100%; height:100%;">${iconHTML}</div>`;
        markerElement.setAttribute("data-icon-type", targetType);
        markerElement.setAttribute("data-selected", currentSelectedState);
        markerElement.setAttribute("data-hash", innerHash);
        markerElement.style.zIndex = zIndex;
      }

      const newTransform = `translate3d(${p.x.toFixed(1)}px, ${p.y.toFixed(1)}px, 0) ${offsetTransform}`;
      if (markerElement._cachedTransform !== newTransform) {
        markerElement.style.transform = newTransform;
        markerElement._cachedTransform = newTransform;
      }
    });

    if (shouldUpdateHeatmap) {
      const centerLngLat = map_object.getCenter();
      const centerPx = map_object.project(centerLngLat);

      heatmapAnchorRef.current = {
        lngLat: centerLngLat,
        px: centerPx,
        zoom: currentZoom,
        bearing: currentBearing,
        pitch: currentPitch,
      };

      if (heatmapCanvasRef.current) {
        const transform = `translate3d(0px, 0px, 0) scale(1)`;
        if (heatmapCanvasRef.current._cachedTransform !== transform) {
          heatmapCanvasRef.current.style.transform = transform;
          heatmapCanvasRef.current._cachedTransform = transform;
        }
      }

      updateHeatmap(hidden_on_screen_points, canvasWidth, canvasHeight);
    } else if (heatmapAnchorRef.current && heatmapCanvasRef.current) {
      const currentAnchorPx = map_object.project(
        heatmapAnchorRef.current.lngLat,
      );
      const tx = currentAnchorPx.x - heatmapAnchorRef.current.px.x;
      const ty = currentAnchorPx.y - heatmapAnchorRef.current.px.y;

      const transform = `translate3d(${tx}px, ${ty}px, 0) scale(1)`;
      if (heatmapCanvasRef.current._cachedTransform !== transform) {
        heatmapCanvasRef.current.style.transform = transform;
        heatmapCanvasRef.current._cachedTransform = transform;
      }
    }

    updatePopupPosition();
  }, [
    map_object,
    is_show_activity,
    is_show_image,
    updatePopupPosition,
    updateHeatmap,
  ]);

  const updatePositions = useCallback(() => {
    if (!map_object || !is_show_activity) {
      rafRef.current = null;
      return;
    }

    const currentZoom = map_object.getZoom();
    const pitch = map_object.getPitch();
    const bearing = map_object.getBearing();
    const is3D = pitch !== 0 || bearing !== 0;

    const canvasObj = map_object.getCanvas();
    const canvasWidth = canvasObj.clientWidth;
    const canvasHeight = canvasObj.clientHeight;

    const hidden_on_screen_points = [];

    // 1. UPDATE MARKERS & RE-PROJECT ALL POINTS
    screenPointsRef.current.forEach((p) => {
      const coords = p.feature.geometry?.coordinates || [0, 0];
      const point = map_object.project(coords);

      p.x = point.x;
      p.y = point.y;

      p.isOffScreen =
        p.x < -CANVAS_BUFFER ||
        p.x > canvasWidth + CANVAS_BUFFER ||
        p.y < -CANVAS_BUFFER ||
        p.y > canvasHeight + CANVAS_BUFFER;

      if (!p.isVisible && !p.isOffScreen) {
        hidden_on_screen_points.push({ x: p.x, y: p.y });
      }

      const markerElement = markersRef.current[`marker_act_${p.index}`];

      if (!markerElement || markerElement.style.display === "none") return;

      const currentType = markerElement.getAttribute("data-icon-type");
      let offsetTransform = "translate(-50%, -100%)";
      if (currentType === "circle_activity") {
        offsetTransform = "translate(-50%, -50%)";
      }

      const newTransform = `translate3d(${point.x.toFixed(1)}px, ${point.y.toFixed(1)}px, 0) ${offsetTransform}`;
      if (markerElement._cachedTransform !== newTransform) {
        markerElement.style.transform = newTransform;
        markerElement._cachedTransform = newTransform;
      }
    });

    // 2. UPDATE HEATMAP CANVAS
    if (is3D) {
      // Bypassing CSS transforms and forcing native redraw to avoid 3D perspective drift
      if (heatmapCanvasRef.current) {
        const transform = `translate3d(0px, 0px, 0) scale(1)`;
        if (heatmapCanvasRef.current._cachedTransform !== transform) {
          heatmapCanvasRef.current.style.transform = transform;
          heatmapCanvasRef.current._cachedTransform = transform;
        }
      }
      updateHeatmap(hidden_on_screen_points, canvasWidth, canvasHeight);

      // Keep tracking anchor to transition smoothly back to flat 2D panning later
      heatmapAnchorRef.current = {
        lngLat: map_object.getCenter(),
        px: map_object.project(map_object.getCenter()),
        zoom: currentZoom,
        bearing: bearing,
        pitch: pitch,
      };
    } else {
      const useFastHeatmap = heatmapAnchorRef.current;

      if (useFastHeatmap) {
        const scale = Math.pow(2, currentZoom - heatmapAnchorRef.current.zoom);
        const currentAnchorPx = map_object.project(
          heatmapAnchorRef.current.lngLat,
        );
        const tx = currentAnchorPx.x - heatmapAnchorRef.current.px.x * scale;
        const ty = currentAnchorPx.y - heatmapAnchorRef.current.px.y * scale;

        if (heatmapCanvasRef.current) {
          const transform = `translate3d(${tx}px, ${ty}px, 0) scale(${scale})`;
          if (heatmapCanvasRef.current._cachedTransform !== transform) {
            heatmapCanvasRef.current.style.transform = transform;
            heatmapCanvasRef.current._cachedTransform = transform;
          }
        }
      }
    }

    updatePopupPosition();
    rafRef.current = null;
  }, [map_object, is_show_activity, updatePopupPosition, updateHeatmap]);

  useEffect(() => {
    updateCollisionsAndRender();
  }, [
    feature_activity_selected,
    multi_activity_selected,
    is_show_activity,
    is_show_image,
    updateCollisionsAndRender,
  ]);

  useEffect(() => {
    if (!map_object || !features_activities_app) return;

    const initializePoints = () => {
      screenPointsRef.current = features_activities_app.map(
        (feature, index) => ({
          index,
          feature,
          x: 0,
          y: 0,
          base_x: 0,
          base_y: 0,
          isVisible: true,
          isOffScreen: false,
          isOffMarkerScreen: false,
          type: is_show_image ? "L3" : "L2",
          hasStack: false,
          stackedFeatures: [feature],
        }),
      );
      heatmapAnchorRef.current = null;
    };

    if (
      screenPointsRef.current.length === 0 &&
      features_activities_app.length > 0
    ) {
      initializePoints();
    }

    const container = map_object.getContainer();
    const existing_canvas = container.querySelector(".activity-heatmap-canvas");
    if (!existing_canvas) {
      const { canvas, ctx } = createHeatmapCanvas(container);
      heatmapCanvasRef.current = canvas;
      heatmapCtxRef.current = ctx;
    } else {
      heatmapCanvasRef.current = existing_canvas;
      heatmapCtxRef.current = existing_canvas.getContext("2d");
    }

    const checkHit = (point) => {
      const candidates = [...screenPointsRef.current].reverse();
      let hitFeature = null;

      for (const p of candidates) {
        if (!p.isVisible || p.isOffMarkerScreen) continue;

        const dx = point.x - p.x;
        const dy = point.y - p.y;

        let isHit = false;

        if (p.type === "L3") {
          isHit = dx >= -L3_W / 2 && dx <= L3_W / 2 && dy >= -L3_H && dy <= 0;
        } else if (p.type === "L2") {
          isHit = dx >= -L2_W / 2 && dx <= L2_W / 2 && dy >= -L2_H && dy <= 0;
        } else {
          const hit_w = Math.max(L1_W, 30);
          const hit_h = Math.max(L1_H, 30);
          isHit =
            dx >= -hit_w / 2 &&
            dx <= hit_w / 2 &&
            dy >= -hit_h / 2 &&
            dy <= hit_h / 2;
        }

        if (isHit) {
          hitFeature = p;
          break;
        }
      }

      if (hitFeature) {
        // Tag the shared map object to claim priority over other layers
        map_object._activity_hit_time = Date.now();

        if (hitFeature.hasStack) on_click_double(hitFeature.stackedFeatures);
        else on_click_single(hitFeature.feature);
      } else {
        on_click_empty();
      }
    };

    const handleMapClick = (e) => {
      if (isTouchModeRef.current) {
        isTouchModeRef.current = false;
        return;
      }
      if (!isDraggingRef.current) checkHit(e.point);
    };

    const handleTouchStart = (e) => {
      isTouchModeRef.current = true;
      if (e.points && e.points.length > 1) {
        touchStateRef.current.is_multi = true;
      } else if (e.points && e.points.length === 1) {
        touchStateRef.current.is_multi = false;
        touchStateRef.current.start = e.point;
        touchStateRef.current.time = Date.now();
      }
    };

    const handleTouchEnd = (e) => {
      if (touchStateRef.current.is_multi) return;

      if (touchStateRef.current.start && e.point) {
        const dx = e.point.x - touchStateRef.current.start.x;
        const dy = e.point.y - touchStateRef.current.start.y;
        const dist_sq = dx * dx + dy * dy;
        const now = Date.now();
        const duration = now - touchStateRef.current.time;

        if (dist_sq < 100 && duration < 500) {
          const timeSinceLastTap = now - touchStateRef.current.lastTapTime;

          if (timeSinceLastTap < 300) {
            if (touchStateRef.current.tapTimeout) {
              clearTimeout(touchStateRef.current.tapTimeout);
              touchStateRef.current.tapTimeout = null;
            }
            touchStateRef.current.lastTapTime = now;
            return;
          }

          touchStateRef.current.lastTapTime = now;
          const hitPoint = { x: e.point.x, y: e.point.y };

          touchStateRef.current.tapTimeout = setTimeout(() => {
            checkHit(hitPoint);
            touchStateRef.current.tapTimeout = null;
          }, 300);
        }
      }
      touchStateRef.current.start = null;
    };

    const onMapMove = () => {
      if (!rafRef.current) {
        rafRef.current = requestAnimationFrame(updatePositions);
      }

      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        updateCollisionsAndRender();
      }, CLUSTERING_DEBOUNCE_MS);
    };

    const onMoveStart = () => {
      isDraggingRef.current = true;
    };

    const onMoveEnd = () => {
      isDraggingRef.current = false;

      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }

      updateCollisionsAndRender();
    };

    const attachAllListeners = () => {
      initializePoints();
      updateCollisionsAndRender();

      map_object.on("move", onMapMove);
      map_object.on("zoom", onMapMove);
      map_object.on("resize", onMapMove);
      map_object.on("rotate", onMapMove);
      map_object.on("pitch", onMapMove);
      map_object.on("movestart", onMoveStart);
      map_object.on("moveend", onMoveEnd);
      map_object.on("zoomend", onMoveEnd);
      map_object.on("rotateend", onMoveEnd);
      map_object.on("pitchend", onMoveEnd);
      map_object.on("click", handleMapClick);
      map_object.on("touchstart", handleTouchStart);
      map_object.on("touchend", handleTouchEnd);
    };

    const removeAllListeners = () => {
      map_object.off("move", onMapMove);
      map_object.off("zoom", onMapMove);
      map_object.off("resize", onMapMove);
      map_object.off("rotate", onMapMove);
      map_object.off("pitch", onMapMove);
      map_object.off("movestart", onMoveStart);
      map_object.off("moveend", onMoveEnd);
      map_object.off("zoomend", onMoveEnd);
      map_object.off("rotateend", onMoveEnd);
      map_object.off("pitchend", onMoveEnd);
      map_object.off("click", handleMapClick);
      map_object.off("touchstart", handleTouchStart);
      map_object.off("touchend", handleTouchEnd);

      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      if (touchStateRef.current.tapTimeout) {
        clearTimeout(touchStateRef.current.tapTimeout);
        touchStateRef.current.tapTimeout = null;
      }
    };

    const handleStyleData = () => {
      if (map_object.isStyleLoaded()) {
        attachAllListeners();
        map_object.off("styledata", handleStyleData);
      }
    };

    if (map_object.isStyleLoaded()) attachAllListeners();
    else map_object.on("styledata", handleStyleData);

    return () => {
      map_object.off("styledata", handleStyleData);
      removeAllListeners();

      Object.values(markersRef.current).forEach((marker) => {
        if (marker.parentNode) marker.parentNode.removeChild(marker);
      });
      markersRef.current = {};

      if (heatmapCanvasRef.current && heatmapCanvasRef.current.parentNode) {
        heatmapCanvasRef.current.parentNode.removeChild(
          heatmapCanvasRef.current,
        );
      }
      heatmapCanvasRef.current = null;
      heatmapCtxRef.current = null;
      heatmapAnchorRef.current = null;
    };
  }, [
    map_object,
    features_activities_app,
    is_show_image,
    updateCollisionsAndRender,
    updatePositions,
    on_click_single,
    on_click_double,
    on_click_empty,
  ]);

  return (
    <main
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        overflow: "hidden",
      }}
    >
      <div
        ref={popupRef}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          pointerEvents: "auto",
          display: "none",
          willChange: "transform",
        }}
      >
        <div
          style={{
            position: "relative",
            backgroundColor: "white",
            borderRadius: "15px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            width: "280px",
            transformOrigin: "bottom center",
            animation:
              "popupGrow 0.35s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards",
          }}
        >
          <button
            onClick={on_close}
            style={{
              position: "absolute",
              top: "8px",
              right: "8px",
              background: "white",
              border: "none",
              fontSize: "22px",
              cursor: "pointer",
              padding: "4px 8px",
              lineHeight: "1",
              color: "#333",
              zIndex: 10,
              borderRadius: "50%",
            }}
          >
            &times;
          </button>

          <div
            style={{
              maxHeight: "380px",
              overflowY: "auto",
              padding: "16px",
              paddingTop: "32px",
            }}
          >
            {modal_activity && feature_activity_selected && (
              <div>
                <PopupContent
                  feature={feature_activity_selected}
                  on_click_image={on_click_image}
                />
              </div>
            )}

            {modal_activity_double && multi_activity_selected && (
              <div>
                {multi_activity_selected.map((feature, idx) => (
                  <div
                    key={feature?.title || idx}
                    style={{
                      borderBottom: "1px solid #eee",
                      marginBottom: "16px",
                      paddingBottom: "16px",
                    }}
                  >
                    <div className="badge_small background_black margin_bottom">
                      # {idx + 1}
                    </div>
                    <PopupContent
                      feature={feature}
                      on_click_image={on_click_image}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          <div
            style={{
              position: "absolute",
              top: "100%",
              marginTop: "-1px",
              left: "50%",
              transform: "translateX(-50%)",
              width: "0",
              height: "0",
              borderLeft: "14px solid transparent",
              borderRight: "14px solid transparent",
              borderTop: "14px solid white",
              filter: "drop-shadow(0px 2px 2px rgba(0,0,0,0.1))",
            }}
          ></div>
        </div>
      </div>

      <IMAGE_FULL
        isOpen={modal_image_preview}
        mediaList={
          preview_media_list.length > 0
            ? preview_media_list
            : preview_image_url
              ? [preview_image_url]
              : []
        }
        initialIndex={preview_media_index}
        onClose={on_close_image}
      />
    </main>
  );
};

const mapStateToProps = (state) => ({
  layer: state.layer,
  properties: state.properties,
});

export default connect(mapStateToProps, { set_value_properties })(
  LAYER_ACTIVITIES_APP,
);
